import { useEffect, useState } from "react";
import { trackPWAEvent } from "@/utils/pwaAnalytics";
import { usePWA } from "@/hooks/usePWA";

interface SplashHeaderProps {
  puzzleNumber: number;
  formattedDate: string;
  hasGuessed: boolean;
  onHowToPlayClick: () => void;
  onBlogClick: () => void;
  onInstallClick: () => void;
}

export function SplashHeader({
  puzzleNumber,
  formattedDate,
  hasGuessed,
  onHowToPlayClick,
  onBlogClick,
  onInstallClick,
}: SplashHeaderProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { canInstall, isInstalled } = usePWA();

  const handleInstallClick = () => {
    trackPWAEvent('install_clicked', 'splash_header', canInstall, isInstalled);
    onInstallClick();
  };

  // Check for returning visitor and auto-collapse after 3 seconds
  useEffect(() => {
    const isReturning = localStorage.getItem("wormble-visited") === "true";
    
    // First visit: set flag
    if (!isReturning) {
      localStorage.setItem("wormble-visited", "true");
    } else {
      // Returning visitor: auto-collapse after 3 seconds
      const timer = setTimeout(() => setIsCollapsed(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Collapse on first guess
  useEffect(() => {
    if (hasGuessed) {
      setIsCollapsed(true);
    }
  }, [hasGuessed]);

  // Collapse on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Collapsed view - keep install link visible
  if (isCollapsed) {
    return (
      <section
        aria-label="Wormble introduction"
        className="w-full max-w-[350px] mx-auto text-center py-1.5 mb-2 transition-all duration-300"
      >
        <div className="flex items-center justify-center gap-2">
          <p className="text-xs text-muted-foreground">
            Wormble — Daily Puzzle #{puzzleNumber}
          </p>
          {!isInstalled && (
            <>
              <span className="text-muted-foreground text-xs" aria-hidden="true">•</span>
              <button
                onClick={handleInstallClick}
                className="text-xs text-primary hover:underline font-medium animate-pulse"
                aria-label="Install Wormble app"
              >
                📱 Install
              </button>
            </>
          )}
        </div>
      </section>
    );
  }

  // Expanded view
  return (
    <section
      aria-label="Wormble introduction"
      className="w-full max-w-[350px] mx-auto text-center py-3 mb-3 space-y-1 animate-fade-in"
    >
      <h2 className="text-sm text-muted-foreground font-medium">
        Welcome to Wormble — The Daily 5-Try Word Puzzle
      </h2>
      <p className="text-xs text-muted-foreground">
        Today's Puzzle: #{puzzleNumber} • {formattedDate}
      </p>
      <div className="flex justify-center gap-3 pt-1">
        <button
          onClick={onHowToPlayClick}
          className="text-xs text-primary hover:underline font-medium min-h-[44px] py-2 px-3"
          aria-label="Learn how to play Wormble"
        >
          How to Play
        </button>
        <span className="text-muted-foreground" aria-hidden="true">•</span>
        <button
          onClick={onBlogClick}
          className="text-xs text-primary hover:underline font-medium min-h-[44px] py-2 px-3"
          aria-label="Read the Wormble blog"
        >
          Blog
        </button>
        <span className="text-muted-foreground" aria-hidden="true">•</span>
        <button
          onClick={handleInstallClick}
          className="text-xs text-primary hover:underline font-medium min-h-[44px] py-2 px-3"
          aria-label="Install Wormble app"
        >
          📱 Install
        </button>
      </div>
    </section>
  );
}
